import type { FC } from 'react';
import { useCallback, useMemo } from 'react';

import { Box, Skeleton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { shallowEqual } from 'react-redux';
import { useNavigate, useParams } from 'react-router';

import { ChatRoutesPathEnum } from '@setup/router/routes/chat/chatRoutesPath.enum';
import {
  CheckDoubleIcon,
  CrossInCircleRedIcon,
  EyeOpenIcon,
  LeaveIcon,
  PencilIcon,
  TrashIcon,
} from '@setup/svg-icons/svg-icons';
import { AlertModalVariantEnum } from '@shared/configs/modals/alertModal/alertModal.config';
import { AnalyticsEventEnum } from '@shared/enums/analyticsEvent.enum';
import { AvatarSizeEnum } from '@shared/enums/avatarSize.enum';
import { ModalNamesEnum } from '@shared/enums/modalNames.enum';
import useAnalyticsEventTracker from '@shared/hooks/analytics/useAnalyticsEventTracker';
import { useAppDispatch, useAppSelector } from '@shared/hooks/redux';
import modalObserver from '@shared/utils/observers/modalObserver';
import { deleteThreadsItem } from '@store/chat/chatSlice';
import {
  deleteGroupChat,
  getGlobalChatCounters,
  getGroupChatUsers,
  getThreadCounter,
  leaveGroupChat,
  readAllMessages,
} from '@store/chat/chatThunk';
import { AvatarContainer, BaseActionMenu } from 'shared/ui-kit';

import { ChatRightPanelHeaderContainer } from './MessagesHeader.style';

import type { ThreadModel } from '@shared/models/chat/chat.model';

type Props = {
  thread: ThreadModel | null;
  userId: number;
  isGlobalHeader?: boolean;
  handleClearThreadHistory: () => void;
  clearChatCounts?: () => void;
};

const MessagesHeader: FC<Props> = ({
  thread,
  userId,
  isGlobalHeader = false,
  handleClearThreadHistory,
  clearChatCounts,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const profile = useAppSelector((state) => state.profile.data, shallowEqual);
  const { type } = useParams();

  const gaEventTracker = useAnalyticsEventTracker();

  const ownerThread = useMemo(() => {
    if (thread) return thread.all_users.find((user) => user.id !== userId);
    return null;
  }, [thread, userId]);

  const handleLeaveFromChat = useCallback(() => {
    if (thread) {
      gaEventTracker({ event: AnalyticsEventEnum.leaveGroupChatSelected });

      const onLeaveMySelf = () =>
        dispatch(leaveGroupChat(thread.id)).then(() => {
          navigate(`${ChatRoutesPathEnum.chat}/${ChatRoutesPathEnum.group}`);
          dispatch(deleteThreadsItem(thread.id));
          modalObserver.removeModal(ModalNamesEnum.alertModal);
        });

      modalObserver.addModal(ModalNamesEnum.alertModal, {
        props: {
          variant: AlertModalVariantEnum.delete,
          entityName: t('general.yourselfFromTheChat'),
          onClickRightBtn: onLeaveMySelf,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, navigate, t, thread]);

  const handleDeleteChat = useCallback(() => {
    if (thread) {
      gaEventTracker({ event: AnalyticsEventEnum.deleteGroupChatSelected });

      const onClickRightBtn = () => {
        dispatch(deleteGroupChat(thread?.id)).then(() => {
          navigate(`${ChatRoutesPathEnum.chat}/${ChatRoutesPathEnum.group}`);
          dispatch(deleteThreadsItem(thread.id));
          modalObserver.removeModal(ModalNamesEnum.alertModal);
        });
      };

      modalObserver.addModal(ModalNamesEnum.alertModal, {
        props: {
          variant: AlertModalVariantEnum.delete,
          entityName: t('general.chat'),
          onClickRightBtn,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, navigate, t, thread]);

  const handleOpenChatInfoModal = useCallback(() => {
    gaEventTracker({ event: AnalyticsEventEnum.viewGroupChatInfoSelected });
    if (!thread) return;
    dispatch(
      getGroupChatUsers({
        threadId: thread.id,
      }),
    ).then((result) => {
      if (getGroupChatUsers.fulfilled.match(result)) {
        modalObserver.addModal(ModalNamesEnum.groupChatModal, {
          props: {
            thread: { ...thread, users: result.payload },
          },
        });
      }
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thread]);

  const handleReadAllMessages = useCallback(() => {
    if (thread) {
      dispatch(readAllMessages(thread.id)).then(() => {
        dispatch(getThreadCounter(thread.id));
        dispatch(getGlobalChatCounters());
        clearChatCounts?.();
      });
    }
  }, [clearChatCounts, dispatch, thread]);

  const menuList = useMemo(() => {
    const menu = [];

    if (type === 'group') {
      menu.push({
        label:
          thread?.owner_id === profile?.id
            ? t('general.buttons.editChatInfo')
            : t('general.buttons.viewChatInfo'),
        callback: () => handleOpenChatInfoModal(),
        startIcon: thread?.owner_id === profile?.id ? PencilIcon : EyeOpenIcon,
      });
      menu.push({
        label: t('general.actionMenus.readAll'),
        callback: () => handleReadAllMessages(),
        startIcon: CheckDoubleIcon,
      });
      if (thread?.owner_id === profile?.id) {
        menu.push({
          label: t('general.buttons.deleteChat'),
          callback: () => handleDeleteChat(),
          startIcon: TrashIcon,
        });
      }
      menu.push({
        label: t('general.buttons.leaveGroup'),
        callback: () => handleLeaveFromChat(),
        startIcon: LeaveIcon,
      });
    } else if (!isGlobalHeader) {
      menu.push({
        label: t('general.actionMenus.readAll'),
        callback: () => handleReadAllMessages(),
        startIcon: CheckDoubleIcon,
      }),
        menu.push({
          label: t('general.buttons.clearChat'),
          callback: () => handleClearThreadHistory(),
          startIcon: CrossInCircleRedIcon,
        });
    }

    return menu;
  }, [
    handleClearThreadHistory,
    handleDeleteChat,
    handleLeaveFromChat,
    handleOpenChatInfoModal,
    handleReadAllMessages,
    isGlobalHeader,
    profile?.id,
    t,
    thread?.owner_id,
    type,
  ]);

  return (
    <ChatRightPanelHeaderContainer isGlobalHeader={isGlobalHeader}>
      {thread === null ? (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Skeleton variant="circular" height={29} width={29} />
          <Box sx={{ display: 'flex', flexDirection: 'column', marginLeft: '12px' }}>
            <Skeleton sx={{ borderRadius: '5px' }} variant="text" height={19} width={150} />
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <AvatarContainer
            variant={type === 'group' ? 'rounded' : 'circular'}
            firstName={
              type === 'group'
                ? thread.subject
                : thread.is_support || thread.is_system
                  ? thread.subject
                  : ownerThread?.first_name || ''
            }
            lastName={type === 'group' ? thread.subject[1] : ownerThread?.last_name || ''}
            src={
              type === 'group'
                ? thread.avatar?.additional_info?.size_urls?.middle_icon || thread?.avatar?.url || ''
                : thread.avatar?.additional_info?.size_urls?.avatar_icon || thread?.avatar?.url || ''
            }
            id={
              thread.is_support || thread.is_system || type === 'group'
                ? thread.id || 0
                : ownerThread?.id || 0
            }
            size={AvatarSizeEnum.size40}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', marginLeft: '12px', width: '100%' }}>
            <Typography
              sx={{
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 1,
                overflow: 'hidden',
              }}
              color={(theme) => theme.palette.case.neutral.n900}
              variant="s2"
            >
              {thread.subject}
            </Typography>
            {thread && !!thread.all_users.length && type === 'group' && (
              <Typography color={(theme) => theme.palette.case.neutral.n500} variant="t12r">
                {thread.all_users.length > 1
                  ? `${thread.all_users.length} members`
                  : `${thread.all_users.length} member}`}
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {thread && thread.is_system === false && menuList?.length ? (
        <BaseActionMenu iconSize="medium" menuList={menuList} />
      ) : (
        <></>
      )}
    </ChatRightPanelHeaderContainer>
  );
};

export default MessagesHeader;
